import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
// @ts-ignore
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar,
} from 'recharts';
import ExerciseMuscleMap from '../ExerciseMuscleMap';
import type { ChartDataPoint, LastExecutionRow, LastSessionDetail } from './types';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
} from '@mui/material';

interface StrengthChartsProps {
  chartData: ChartDataPoint[];
  lastExecution: LastExecutionRow[] | null;
  lastSessionDetail: LastSessionDetail | null;
  printMuscleValues: Record<string, number>;
  printMuscleWeight: Record<string, number>;
}

const MUSCLE_LABELS: Record<string, string> = {
  chest: 'Гръдни', back: 'Гръб', quadriceps: 'Квадрицепс', hamstrings: 'Задно бедро',
  adductors: 'Аддуктори', shoulders: 'Рамене',
  arms: 'Ръце', core: 'Корем', glutes: 'Глутеуси', calves: 'Икри',
};

const StrengthCharts: React.FC<StrengthChartsProps> = ({
  chartData, lastExecution, lastSessionDetail, printMuscleValues, printMuscleWeight,
}) => {
  if (chartData.length === 0) return null;

  const sortedPrintMuscles = Object.entries(printMuscleValues)
    .filter(([group]) => group !== 'cardio')
    .sort(([, leftValue], [, rightValue]) => Number(rightValue) - Number(leftValue));

  return (
    <>
      {/* Screen: Charts stacked vertically */}
      <Box className="no-print">
        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Обем по време</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="totalVolume" fill="#8884d8" name="Обем (kg)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Максимално Тегло</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="maxWeight" stroke="#28a745" strokeWidth={2} name="Макс Тегло (kg)" dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Повторения и RPE</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left" type="monotone" dataKey="avgReps" stroke="#ffc107" strokeWidth={2} name="Повторения" dot={{ r: 4 }} />
              <Line yAxisId="right" type="monotone" dataKey="avgRPE" stroke="#dc3545" strokeWidth={2} name="RPE" dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Last Training Session */}
      {lastSessionDetail && (
        <Box className="last-session-container no-print" sx={{ width: '100%', mb: 3 }}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
              Последна Тренировка — {new Date(lastSessionDetail.date).toLocaleDateString('bg-BG')}
            </Typography>
            {lastSessionDetail.exercises.map((ex, exIdx) => (
              <Box key={exIdx} sx={{ mb: exIdx < lastSessionDetail.exercises.length - 1 ? 2 : 0 }}>
                {lastSessionDetail.exercises.length > 1 && (
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'primary.main' }}>
                    {ex.exerciseName}
                  </Typography>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Сет</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, py: 0.5 }}>Тегло (kg)</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, py: 0.5 }}>Повторения</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, py: 0.5 }}>RPE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ex.sets.map((set, setIdx) => (
                        <TableRow key={setIdx}>
                          <TableCell sx={{ py: 0.5 }}>
                            {setIdx + 1}
                            {set.assistance && (
                              <Chip label="A" size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, fontWeight: 600 }}>{set.weight}</TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            {set.repsLeft != null && set.repsRight != null
                              ? `Л:${set.repsLeft} / Д:${set.repsRight}`
                              : set.reps}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>{set.rpe ?? '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      {/* Print: Muscle diagram + Data tables */}
      <Box className="print-only-el print-tables progress-print-layout" sx={{ display: 'flex', gap: 1 }}>
        <Box className="print-muscle-map print-muscle-panel" sx={{ flex: '0 0 22%' }}>
          <Typography className="print-section-title" variant="subtitle2">
            Натоварени мускулни групи
          </Typography>
          <ExerciseMuscleMap muscleValues={printMuscleValues} hideAnnotations printMode />
          <table className="print-muscle-legend">
            <tbody>
              {sortedPrintMuscles.map(([group, val]) => {
                const kg = printMuscleWeight[group] || 0;
                return (
                  <tr key={group}>
                    <td>{MUSCLE_LABELS[group] || group}</td>
                    <td>{typeof val === 'number' ? (val % 1 === 0 ? val : val.toFixed(1)) : val}</td>
                    <td style={{ color: '#666' }}>{kg > 0 ? `${Math.round(kg).toLocaleString('bg-BG')} kg` : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>

        <Box className="print-tables-data" sx={{ flex: '0 0 68%', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Paper className="print-data-panel" sx={{ flex: '0 0 48%', p: 1 }}>
            <Typography className="print-section-title" variant="subtitle2">Обем по време</Typography>
            <table className="print-data-table">
              <thead><tr><th>Дата</th><th>Обем (kg)</th><th>Макс Тегло</th></tr></thead>
              <tbody>
                {chartData.map((d, i) => (
                  <tr key={i}><td>{d.date}</td><td><strong>{d.totalVolume}</strong></td><td>{d.maxWeight} kg</td></tr>
                ))}
              </tbody>
            </table>
          </Paper>

          <Paper className="print-data-panel" sx={{ flex: '0 0 49%', p: 1 }}>
            <Typography className="print-section-title" variant="subtitle2">Повторения и RPE</Typography>
            <table className="print-data-table">
              <thead><tr><th>Дата</th><th>Ср. Повт.</th><th>Ср. RPE</th></tr></thead>
              <tbody>
                {chartData.map((d, i) => (
                  <tr key={i}><td>{d.date}</td><td>{d.avgReps}</td><td>{d.avgRPE ?? '-'}</td></tr>
                ))}
              </tbody>
            </table>
          </Paper>

          <Paper className="print-data-panel print-data-panel--full" sx={{ flex: '0 0 99%', p: 1 }}>
            <Typography className="print-section-title" variant="subtitle2">Последно Изпълнение</Typography>
            {lastExecution ? (
              <table className="print-data-table">
                <thead><tr><th>Упражнение</th><th>Сетове</th><th>Макс Тегло</th><th>Общо Повт.</th><th>Ср. RPE</th></tr></thead>
                <tbody>
                  {lastExecution.map((row, i) => (
                    <tr key={i}>
                      <td style={{ textAlign: 'left' }}>{row.exercise}</td>
                      <td>{row.sets}</td>
                      <td>{row.maxWeight} kg</td>
                      <td>{row.totalReps}</td>
                      <td>{row.avgRPE}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Typography variant="caption" color="textSecondary">Няма данни</Typography>
            )}
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default StrengthCharts;
